import React, {useEffect, useState} from 'react';
import {Box, Link, Stack, Typography} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {groupDeviceAlertsByDate} from 'app/components/DevicesAlerts/utils';
import {DevicesAlertsDay} from 'app/components/DevicesAlerts/DevicesAlertsDay';
import {LoadingPlaceholder} from 'app/components/sharedReactComponents/LoadingPlaceholder';
import {Sx} from 'app/types/common';
import {DateAlerts} from 'app/components/DevicesAlerts/types';
import {AnyDeviceModelType} from 'app/components/DeviceDetails/Models/Fabric';
import {router} from 'app/router/main';
import {useTeamAlertsQuery} from 'app/components/entities/alerts';
import {useCloud} from 'app/components/features/cloud';
import {isNil} from 'app/util/isNil';

interface Props extends Sx {
  device: AnyDeviceModelType;
}

export function AlertsTab({sx, device}: Props) {
  const {user} = useCloud();
  const [alertGroups, setAlertGroups] = useState<DateAlerts[]>([]);

  const deviceId = device.getId();
  const query = useTeamAlertsQuery();

  const paymentAccess = user.role.canEditBilling();

  const batchCapability = device.caps.batch;

  /**
   * There is no capability about alerts, so we check the batch capability because it is part of edge
   */
  const delinquent = !isNil(batchCapability)
    ? !batchCapability.enabled && batchCapability.reason === 'subscription_is_dq'
    : false;

  useEffect(() => {
    const filtered = query.data?.filter((a) => a.deviceId.startsWith(deviceId)) ?? [];
    const grouped = groupDeviceAlertsByDate(filtered);
    setAlertGroups(grouped);
  }, [query.data, deviceId]);

  return (
    <Box sx={sx}>
      {delinquent && (
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <ErrorOutlineIcon color="error" />

          <Typography color="text.secondary">
            To receive device alerts by email,{' '}
            {paymentAccess ? (
              <>
                <Link href={router.url('settingBillingUsage')}>update your payment method</Link> to
                restore access.
              </>
            ) : (
              'contact your Epiphan Cloud team owner to update the billing information and regain access.'
            )}
          </Typography>
        </Stack>
      )}

      <Typography variant="h5" fontWeight={600} mb={1}>
        Device Alerts
      </Typography>

      <Typography mb={1}>
        <Link href={router.url('alerts')}>Manage alerts</Link> to select what alerts you would like
        to receive.
      </Typography>

      {query.isSuccess ? (
        <Stack gap={2}>
          {alertGroups.map((group, index) => (
            <DevicesAlertsDay key={group.date} group={group} showHeader={index === 0} />
          ))}
        </Stack>
      ) : (
        <LoadingPlaceholder />
      )}
    </Box>
  );
}
